// Building blocks of a world (ADR 0020): districts, landmarks, and the ready-made worlds of themes and official tracks.
// A player or the AI names a preset and adjusts it: the short form is the good form.
import type { LandmarkPart, WorldContent, WorldDistrict } from '@gruvetto/contracts';

type Preset = WorldDistrict['preset'];
type Surface = NonNullable<WorldContent['stretches']>[number]['surface'];
type Edge = NonNullable<WorldContent['stretches']>[number]['edge'];
export interface Flora {
  oak: number;
  pine: number;
  poplar: number;
  bush: number;
  palm: number;
  cactus: number;
  rock: number;
}
/** A district with every value resolved. */
export interface DistrictStyle {
  preset: Preset;
  floors: [number, number];
  density: number;
  setback: number;
  /** Rows of buildings behind the road. */
  rows: number;
  /** Plot width along the road and depth (m). */
  plot: [number, number];
  facades: string[];
  roofs: string[];
  windows: NonNullable<WorldDistrict['windows']>;
  roof: NonNullable<WorldDistrict['roof']>;
  shops: boolean;
  /** Ground colour and how much it replaces the mood's grass. */
  ground: string | null;
  groundMix: number;
  /** Stripes or a patchwork of the mood's fields far from the road. */
  fields: boolean;
  vines: boolean;
  trees: number;
  grass: number;
  flowers: number;
  flora: Flora;
  relief: number;
  lamps: boolean;
  cars: boolean;
  streetTrees: boolean;
  surface: NonNullable<Surface>;
  edge: NonNullable<Edge>;
  crowd: number;
}

const NO_FLORA: Flora = { oak: 0, pine: 0, poplar: 0, bush: 0, palm: 0, cactus: 0, rock: 0 };
const OPEN: Omit<DistrictStyle, 'preset'> = {
  floors: [1, 2],
  density: 0,
  setback: 10,
  rows: 1,
  plot: [14, 12],
  facades: ['#d8ccb2'],
  roofs: ['#7a3a2c'],
  windows: 'shutters',
  roof: 'gable',
  shops: false,
  ground: null,
  groundMix: 0,
  fields: false,
  vines: false,
  trees: 1,
  grass: 1,
  flowers: 1,
  flora: { ...NO_FLORA, oak: 0.6, bush: 0.3, rock: 0.1 },
  relief: 1,
  lamps: false,
  cars: false,
  streetTrees: false,
  surface: 'asphalt',
  edge: 'verge',
  crowd: 1,
};
const BRICK = ['#9a5040', '#8a4636', '#b0634a', '#7d5a4a'],
  RENDER = ['#e9e3d7', '#d8ccb2', '#c9b79a', '#e3d6bf'],
  SLATE = ['#3f434b', '#4a4e57'],
  TILE = ['#7a3a2c', '#8b4530', '#a0523a'];
const URBAN = {
  ground: '#a3a19b',
  groundMix: 0.95,
  fields: false,
  grass: 0,
  flowers: 0,
  relief: 0.08,
  lamps: true,
  cars: true,
  streetTrees: true,
  surface: 'asphalt',
  edge: 'pavement',
  crowd: 0.8,
} as const;

export const DISTRICTS: Record<Preset, DistrictStyle> = {
  countryside: {
    ...OPEN,
    preset: 'countryside',
    fields: true,
    density: 0.02,
    facades: RENDER,
    roofs: TILE,
    flora: { ...NO_FLORA, oak: 0.55, bush: 0.25, poplar: 0.12, rock: 0.08 },
  },
  vineyard: {
    ...OPEN,
    preset: 'vineyard',
    fields: true,
    vines: true,
    ground: '#7b6a45',
    groundMix: 0.45,
    trees: 0.35,
    density: 0.015,
    facades: ['#e8d9b5', '#d9c49a'],
    roofs: TILE,
    relief: 1.1,
    flora: { ...NO_FLORA, oak: 0.3, bush: 0.4, poplar: 0.2, rock: 0.1 },
  },
  forest: {
    ...OPEN,
    preset: 'forest',
    trees: 2,
    flowers: 0.3,
    relief: 1.2,
    ground: '#3e4f28',
    groundMix: 0.55,
    flora: { ...NO_FLORA, oak: 0.5, pine: 0.3, bush: 0.15, rock: 0.05 },
  },
  alpine: {
    ...OPEN,
    preset: 'alpine',
    trees: 0.9,
    relief: 2,
    flowers: 0.6,
    edge: 'guardrail',
    density: 0.008,
    facades: ['#6f5138', '#8a6a48'],
    roofs: SLATE,
    roof: 'steep',
    flora: { ...NO_FLORA, pine: 0.75, bush: 0.08, rock: 0.17 },
  },
  meadow: { ...OPEN, preset: 'meadow', trees: 0.35, flowers: 2, grass: 1.5, flora: { ...NO_FLORA, oak: 0.7, bush: 0.25, rock: 0.05 } },
  desert: {
    ...OPEN,
    preset: 'desert',
    trees: 0.3,
    grass: 0,
    flowers: 0,
    ground: '#c79a62',
    groundMix: 0.9,
    relief: 1.2,
    edge: 'none',
    flora: { ...NO_FLORA, cactus: 0.5, rock: 0.5 },
  },
  beach: {
    ...OPEN,
    preset: 'beach',
    trees: 0.5,
    grass: 0.2,
    flowers: 0,
    ground: '#d9c493',
    groundMix: 0.92,
    relief: 0.15,
    density: 0.05,
    facades: ['#f4efe4', '#bfe0e4', '#f2d8b8'],
    roofs: ['#c96f4a'],
    roof: 'flat',
    windows: 'shutters',
    edge: 'railing',
    flora: { ...NO_FLORA, palm: 0.8, bush: 0.2 },
  },
  park: { ...OPEN, preset: 'park', trees: 0.8, flowers: 1.6, relief: 0.2, lamps: true, edge: 'kerb', flora: { ...NO_FLORA, oak: 0.7, poplar: 0.1, bush: 0.2 } },
  village: {
    ...OPEN,
    preset: 'village',
    density: 0.6,
    floors: [1, 3],
    setback: 4,
    plot: [11, 10],
    facades: RENDER,
    roofs: TILE,
    roof: 'steep',
    trees: 0.6,
    relief: 0.35,
    grass: 0.4,
    flowers: 0.5,
    edge: 'pavement',
    lamps: true,
    crowd: 1.2,
    flora: { ...NO_FLORA, oak: 0.6, bush: 0.4 },
  },
  'old-town': {
    ...OPEN,
    ...URBAN,
    preset: 'old-town',
    density: 0.95,
    floors: [3, 5],
    setback: 3.4,
    rows: 3,
    plot: [8, 11],
    facades: [...BRICK, ...RENDER],
    roofs: [...SLATE, ...TILE],
    windows: 'arched',
    roof: 'steep',
    shops: true,
    trees: 0.08,
    streetTrees: false,
    surface: 'cobbles',
    crowd: 1.3,
    flora: { ...NO_FLORA, oak: 1 },
  },
  suburb: {
    ...OPEN,
    preset: 'suburb',
    density: 0.7,
    floors: [1, 2],
    setback: 9,
    rows: 2,
    plot: [14, 11],
    facades: [...RENDER, ...BRICK],
    roofs: [...SLATE, ...TILE],
    roof: 'hip',
    trees: 0.7,
    grass: 0.5,
    flowers: 0.5,
    relief: 0.2,
    edge: 'pavement',
    lamps: true,
    cars: true,
    flora: { ...NO_FLORA, oak: 0.6, bush: 0.4 },
  },
  downtown: {
    ...OPEN,
    ...URBAN,
    preset: 'downtown',
    density: 0.95,
    floors: [5, 14],
    setback: 3.6,
    rows: 4,
    plot: [17, 15],
    facades: ['#c9c2b4', '#a9a6a0', '#b3705a', '#8d97a3', '#dcd6ca'],
    roofs: ['#55585e'],
    windows: 'grid',
    roof: 'flat',
    shops: true,
    trees: 0.05,
    flora: { ...NO_FLORA, oak: 1 },
  },
  skyscrapers: {
    ...OPEN,
    ...URBAN,
    preset: 'skyscrapers',
    density: 0.9,
    floors: [14, 48],
    setback: 5,
    rows: 4,
    plot: [26, 24],
    facades: ['#7f97ad', '#5f7485', '#9fb4c4', '#47566a', '#b6c2cc'],
    roofs: ['#3a3d44'],
    windows: 'glass',
    roof: 'flat',
    shops: true,
    trees: 0.04,
    flora: { ...NO_FLORA, oak: 1 },
  },
  industrial: {
    ...OPEN,
    preset: 'industrial',
    density: 0.7,
    floors: [2, 4],
    setback: 8,
    rows: 3,
    plot: [34, 24],
    facades: ['#8c8a84', '#6f7377', '#a0876a', '#55636e'],
    roofs: ['#4c4f55'],
    windows: 'ribbon',
    roof: 'flat',
    ground: '#6e6a64',
    groundMix: 0.85,
    trees: 0.1,
    grass: 0.1,
    flowers: 0,
    relief: 0.1,
    edge: 'fence',
    lamps: true,
    flora: { ...NO_FLORA, bush: 0.7, poplar: 0.3 },
  },
  port: {
    ...OPEN,
    preset: 'port',
    density: 0.65,
    floors: [1, 3],
    setback: 7,
    rows: 2,
    plot: [13, 6],
    facades: ['#b5432e', '#2f6d8c', '#c79a2c', '#3f7a54', '#8d8f93'],
    roofs: ['#55585e'],
    windows: 'none',
    roof: 'flat',
    ground: '#74757a',
    groundMix: 0.92,
    trees: 0,
    grass: 0,
    flowers: 0,
    relief: 0.03,
    surface: 'concrete',
    edge: 'railing',
    lamps: true,
    flora: { ...NO_FLORA },
  },
  wasteland: {
    ...OPEN,
    preset: 'wasteland',
    trees: 0.15,
    grass: 0.2,
    flowers: 0,
    ground: '#5d564b',
    groundMix: 0.8,
    surface: 'worn',
    edge: 'none',
    relief: 1.3,
    flora: { ...NO_FLORA, rock: 0.8, bush: 0.2 },
  },
  empty: { ...OPEN, preset: 'empty', trees: 0, grass: 0, flowers: 0, edge: 'none', relief: 1, flora: { ...NO_FLORA } },
};

const P = (
  shape: LandmarkPart['shape'],
  x: number,
  y: number,
  z: number,
  w: number,
  h: number,
  d: number,
  color: string,
  more: Partial<LandmarkPart> = {},
): LandmarkPart => ({ shape, x, y, z, w, h, d, color, ...more });
const STONE = '#b9b2a4',
  DARK = '#3c3f46',
  WHITE = '#efeee8';

/** `main` is replaced by the landmark's `color`. y is the bottom of the part, except spinning parts (their centre). */
export const LANDMARKS: Record<NonNullable<import('@gruvetto/contracts').WorldLandmark['preset']>, LandmarkPart[]> = {
  windmill: [
    P('cylinder', 0, 0, 0, 7, 14, 7, 'main'),
    P('cone', 0, 14, 0, 8, 4, 8, DARK),
    P('box', 0, 15, 4.3, 22, 0.8, 0.3, '#d9cfb8', { spin: true }),
    P('box', 0, 15, 4.3, 0.8, 22, 0.3, '#d9cfb8', { spin: true }),
  ],
  church: [
    P('box', 0, 0, -8, 12, 10, 24, 'main'),
    P('pyramid', 0, 10, -8, 13, 6, 25, '#5a4a42'),
    P('box', 0, 0, 7, 7, 26, 7, 'main'),
    P('pyramid', 0, 26, 7, 8, 14, 8, DARK),
    P('sphere', 0, 40, 7, 0.8, 0.8, 0.8, '#d9b64a', { finish: 'metal' }),
  ],
  castle: [
    P('box', 0, 0, 0, 34, 12, 26, 'main'),
    ...[
      [-17, -13],
      [17, -13],
      [-17, 13],
      [17, 13],
    ].flatMap(([x, z]) => [P('cylinder', x, 0, z, 9, 20, 9, 'main'), P('cone', x, 20, z, 10, 8, 10, '#3e4a63')]),
    P('box', 0, 12, 0, 14, 12, 12, 'main'),
    P('arch', 0, 0, 13.1, 7, 7, 1, DARK),
  ],
  lighthouse: [
    P('cylinder', 0, 0, 0, 8, 12, 8, WHITE),
    P('cylinder', 0, 12, 0, 7, 12, 7, 'main'),
    P('cylinder', 0, 24, 0, 6, 10, 6, WHITE),
    P('cylinder', 0, 34, 0, 5, 3, 5, '#ffe9a0', { finish: 'glow' }),
    P('cone', 0, 37, 0, 6, 3, 6, DARK),
  ],
  stadium: [
    P('cylinder', 0, 0, 0, 130, 16, 100, 'main'),
    P('torus', 0, 18, 0, 134, 4, 104, WHITE, { tilt: 90 }),
    P('cylinder', 0, 16, 0, 96, 0.5, 66, '#3a7d36'),
  ],
  tower: [
    P('cylinder', 0, 0, 0, 7, 70, 7, STONE),
    P('cylinder', 0, 66, 0, 22, 8, 22, 'main', { finish: 'metal' }),
    P('cone', 0, 74, 0, 3, 34, 3, '#d8d8dc', { finish: 'metal' }),
  ],
  obelisk: [P('box', 0, 0, 0, 12, 2.5, 12, STONE), P('box', 0, 2.5, 0, 5, 30, 5, 'main'), P('pyramid', 0, 32.5, 0, 5, 4, 5, '#d9b64a', { finish: 'metal' })],
  'ferris-wheel': [
    P('torus', 0, 26, 0, 46, 1.2, 1.2, 'main', { spin: true, finish: 'glow' }),
    P('box', 0, 26, 0, 46, 0.6, 0.6, WHITE, { spin: true }),
    P('box', 0, 26, 0, 0.6, 46, 0.6, WHITE, { spin: true }),
    P('box', -6, 0, -2.5, 1, 27, 1, DARK, { tilt: -12 }),
    P('box', 6, 0, -2.5, 1, 27, 1, DARK, { tilt: 12 }),
    P('box', -6, 0, 2.5, 1, 27, 1, DARK, { tilt: -12 }),
    P('box', 6, 0, 2.5, 1, 27, 1, DARK, { tilt: 12 }),
  ],
  'wind-turbine': [
    P('cylinder', 0, 0, 0, 3, 62, 3, WHITE),
    P('box', 0, 62, 1.8, 3, 3, 8, WHITE),
    // a spinning cone grows out of the hub: one blade
    P('cone', 0, 63.5, 6, 2, 24, 0.6, WHITE, { spin: true }),
    P('cone', 0, 63.5, 6, 2, 24, 0.6, WHITE, { spin: true, tilt: 120 }),
    P('cone', 0, 63.5, 6, 2, 24, 0.6, WHITE, { spin: true, tilt: -120 }),
  ],
  gantry: [
    P('box', -7, 0, 0, 0.8, 8, 0.8, DARK),
    P('box', 7, 0, 0, 0.8, 8, 0.8, DARK),
    P('box', 0, 7, 0, 15.5, 1.4, 0.8, 'main'),
    P('box', 0, 6.7, 0, 15, 0.2, 1, '#fff2c0', { finish: 'glow' }),
  ],
  torii: [
    P('cylinder', -6.4, 0, 0, 1.1, 9, 1.1, 'main'),
    P('cylinder', 6.4, 0, 0, 1.1, 9, 1.1, 'main'),
    P('box', 0, 7, 0, 15, 0.8, 0.9, 'main'),
    P('box', 0, 9, 0, 18, 1.1, 1.3, 'main'),
    P('box', 0, 10.1, 0, 19.5, 0.5, 1.6, DARK),
  ],
  pyramid: [P('pyramid', 0, 0, 0, 90, 56, 90, 'main')],
  'water-tower': [
    ...[
      [-4, -4],
      [4, -4],
      [-4, 4],
      [4, 4],
    ].map(([x, z]) => P('cylinder', x, 0, z, 0.8, 16, 0.8, DARK)),
    P('cylinder', 0, 16, 0, 12, 8, 12, 'main'),
    P('cone', 0, 24, 0, 13, 3, 13, DARK),
  ],
  'palm-group': [-3, 2.5, 0].flatMap((x, i) => [
    P('cylinder', x, 0, i * 2 - 2, 0.5, 9 + i, 0.5, '#7a5d3e'),
    P('sphere', x, 9 + i, i * 2 - 2, 5.5, 1.6, 5.5, '#3f7a35'),
  ]),
  'rock-arch': [P('arch', 0, 0, 0, 32, 20, 10, 'main')],
};
/** Default colour of `main` in a preset. */
export const LANDMARK_COLORS: Partial<Record<keyof typeof LANDMARKS, string>> = {
  windmill: '#e7dfcc',
  church: '#cfc6b3',
  castle: '#a59d8e',
  lighthouse: '#c9342c',
  stadium: '#8f949c',
  tower: '#c9ced6',
  obelisk: '#cfc6b3',
  'ferris-wheel': '#ffcf4a',
  gantry: '#e2261d',
  torii: '#c8321f',
  pyramid: '#d3b57c',
  'water-tower': '#7da0b5',
  'rock-arch': '#9a5a3a',
};

/** The worlds behind a track's `theme`: what a track without its own world gets. */
export const THEME_WORLDS: Record<string, WorldContent> = {
  campagne: {
    base: 'countryside',
    horizon: 'hills',
    water: [{ kind: 'lake', at: 0.3, side: 'right', distance: 60, size: 160 }],
    stretches: [{ from: 0.02, to: 0.09, district: 'village' }],
  },
  montagne: { base: 'alpine', horizon: 'mountains', water: [{ kind: 'river', at: 0.6, size: 18 }], stretches: [{ from: 0.2, to: 0.27, structure: 'cutting' }] },
  ville: { base: 'downtown', horizon: 'skyline', water: [{ kind: 'river', at: 0.5, size: 70 }], landmarks: [{ preset: 'church', at: 0.2, side: 'right' }] },
  cote: {
    base: 'beach',
    horizon: 'flat',
    water: [{ kind: 'sea', at: 0.25, distance: 30 }],
    stretches: [{ from: 0.55, to: 0.72, district: 'port' }],
    landmarks: [{ preset: 'lighthouse', at: 0.25, side: 'left' }],
  },
  foret: { base: 'forest', horizon: 'hills', water: [{ kind: 'river', at: 0.7, size: 14 }] },
};

/** The official tracks' worlds, by track id. */
export const OFFICIAL_WORLDS: Record<string, WorldContent> = {
  'everest-road': { base: 'alpine', horizon: 'mountains', stretches: [{ from: 0, to: 1, surface: 'worn' }], districts: [] },
  collines: {
    base: 'countryside',
    horizon: 'mountains',
    water: [{ kind: 'lake', at: 0.82, side: 'left', distance: 40, size: 170 }],
    stretches: [
      { from: 0.02, to: 0.11, district: 'village', crowd: 1.6 },
      { from: 0.28, to: 0.55, district: 'vineyard' },
    ],
    landmarks: [
      { preset: 'windmill', at: 0.47, side: 'right', distance: 25 },
      { preset: 'church', at: 0.07, side: 'left', distance: 28 },
    ],
  },
  mur: {
    base: 'meadow',
    horizon: 'hills',
    water: [{ kind: 'river', at: 0.2, size: 26 }],
    stretches: [
      { from: 0.48, to: 0.68, surface: 'cobbles', markings: false, district: 'old-town', crowd: 2 },
      { from: 0.92, to: 1, district: 'village' },
      { from: 0, to: 0.08, district: 'village' },
    ],
    landmarks: [
      { preset: 'church', at: 0.58, side: 'right', distance: 30 },
      { preset: 'wind-turbine', at: 0.3, side: 'right', distance: 110, every: 240, until: 0.46 },
    ],
  },
  nuages: {
    base: 'alpine',
    horizon: 'mountains',
    water: [{ kind: 'lake', at: 0.05, side: 'left', distance: 50, size: 150 }],
    stretches: [
      { from: 0.3, to: 0.37, structure: 'tunnel', edge: 'wall', markings: true },
      { from: 0.45, to: 0.52, surface: 'worn', district: 'village' },
      { from: 0.62, to: 0.68, structure: 'bridge', bridge: 'stone' },
    ],
    landmarks: [{ preset: 'gantry', at: 0.49, over: true, color: '#1f6fe0' }],
  },
  crit: {
    base: 'downtown',
    horizon: 'skyline',
    water: [{ kind: 'river', at: 0.28, size: 60 }],
    stretches: [
      { from: 0.42, to: 0.72, district: 'skyscrapers', surface: 'smooth' },
      { from: 0.72, to: 0.92, district: 'old-town', surface: 'brick', markings: false },
      { from: 0.92, to: 1, edge: 'race', crowd: 2 },
      { from: 0, to: 0.08, edge: 'race', crowd: 2 },
    ],
    landmarks: [
      { preset: 'tower', at: 0.55, side: 'left', distance: 70 },
      { preset: 'gantry', at: 0.75, over: true },
      { preset: 'ferris-wheel', at: 0.18, side: 'right', distance: 45 },
    ],
  },
};
